import {
  type ConferenceEventProperties,
  type ConferenceJsonLdProps,
  type ConferencePage,
  type ConferenceSpeaker,
} from '@/lib/conferenceStructuredData.types';
import { formatNumber } from '@ingeniorforeningen/figurine-core';

type Language = 'da' | 'en';

const absoluteUrl = (path: string, domain: string): string => {
  try {
    return new URL(path, domain).toString();
  } catch {
    return path;
  }
};

export const getAbsoluteSiteUrl = (path: string, domain: string, locale?: string): string => {
  const localePrefix = locale && locale !== 'da' ? `/${locale}` : '';
  const isAlreadyLocalized =
    localePrefix && (path === localePrefix || path.startsWith(`${localePrefix}/`));
  const localizedPath =
    localePrefix && path.startsWith('/') && !isAlreadyLocalized ? `${localePrefix}${path}` : path;

  return absoluteUrl(localizedPath, domain);
};

const getPerformers = (pages: ConferencePage[], domain: string, locale?: string) => {
  const speakers =
    pages.flatMap((page) =>
      (page.properties?.components?.items || []).flatMap((component) =>
        (component.content?.properties?.programEntries?.items || []).flatMap(
          (entry) => entry.content?.properties?.speakers || [],
        ),
      ),
    ) || [];

  const uniqueSpeakers = new Map<string, ConferenceSpeaker>();
  speakers.forEach((speaker) => {
    const name = speaker.properties?.header?.trim();
    if (name) {
      uniqueSpeakers.set(speaker.route?.path || name, speaker);
    }
  });

  return Array.from(uniqueSpeakers.values()).map((speaker) => {
    const { header, image, title } = speaker.properties || {};
    const profilePath = speaker.route?.path;

    return {
      '@type': 'Person',
      name: header,
      ...(title && { jobTitle: title }),
      ...(image?.[0]?.url && { image: absoluteUrl(image[0].url, domain) }),
      ...(profilePath && { url: getAbsoluteSiteUrl(profilePath, domain, locale) }),
    };
  });
};

const getOffers = (
  signupPage: ConferenceEventProperties['signupPage'],
  pages: ConferencePage[],
  eventUrl: string,
  language: Language,
) => {
  const signupPaths = new Set(signupPage?.flatMap((page) => page.route?.path || []) || []);
  const priceRows =
    pages
      .filter((page) => page.route?.path && signupPaths.has(page.route.path))
      .flatMap((page) =>
        (page.properties?.components?.items || [])
          .filter((component) => component.content?.contentType === 'shared_priceTable')
          .flatMap((component) => component.content?.properties?.rows?.items || []),
      ) || [];

  return priceRows.flatMap((row) => {
    const { header, price, teaser } = row.content?.properties || {};
    const normalizedPrice = String(price ?? '')
      .trim()
      .replace(',', '.');

    if (!/^\d+(?:\.\d{1,2})?$/.test(normalizedPrice)) {
      return [];
    }

    return [
      {
        '@type': 'Offer',
        ...(header && { name: header.trim() }),
        ...(teaser && { description: teaser }),
        price: formatNumber({ value: normalizedPrice, language }),
        numericPrice: Number(normalizedPrice),
        priceCurrency: 'DKK',
        url: eventUrl,
      },
    ];
  });
};

const getAggregateOffer = (
  signupPage: ConferenceEventProperties['signupPage'],
  pages: ConferencePage[],
  eventUrl: string,
  locale?: string,
) => {
  const language: Language = locale?.toLowerCase().startsWith('en') ? 'en' : 'da';
  const offersWithNumericPrice = getOffers(signupPage, pages, eventUrl, language);

  if (offersWithNumericPrice.length === 0) {
    return undefined;
  }

  const prices = offersWithNumericPrice.map((offer) => offer.numericPrice);
  const lowPrice = Math.min(...prices);
  const highPrice = Math.max(...prices);
  const hasPriceRange = highPrice > lowPrice;
  const formattedLowPrice = formatNumber({ value: lowPrice, language, format: 'currency' });
  const priceText = `${hasPriceRange ? (language === 'en' ? 'From ' : 'Fra ') : ''}${formattedLowPrice}`;
  const offers = offersWithNumericPrice.map(({ numericPrice: _numericPrice, ...offer }) => offer);

  return {
    '@type': 'AggregateOffer',
    lowPrice: lowPrice.toFixed(2),
    highPrice: highPrice.toFixed(2),
    priceCurrency: 'DKK',
    priceText,
    offerCount: offers.length,
    offers,
  };
};

const ConferenceJsonLd = ({
  conferenceJsonLdData,
  locale,
  pagePath,
  properties,
}: ConferenceJsonLdProps) => {
  const events = properties.events?.items || [];
  const domain = process.env.NEXT_PUBLIC_IDA_DOMAIN || 'https://ida.dk';
  const pageUrl = getAbsoluteSiteUrl(pagePath, domain, locale);
  const name = properties.metaTitle || properties.header;
  const description = properties.metaDescription || properties.teaser;
  const image = properties.metaImage?.[0]?.url || properties.image?.[0]?.url;
  const pages = conferenceJsonLdData?.length ? conferenceJsonLdData : [{ properties }];
  const performers = getPerformers(pages, domain, locale);

  const graph = events.flatMap((item, index) => {
    const event = item.content?.properties;
    const startDate = event?.date?.match(/^\d{4}-\d{2}-\d{2}/)?.[0];

    if (!event || !startDate || !name) {
      return [];
    }

    const signupPath = event.signupPage?.[0]?.route?.path;
    const eventUrl = signupPath ? getAbsoluteSiteUrl(signupPath, domain, locale) : pageUrl;
    const isOnline = event.venueName?.trim().toLowerCase() === 'online';
    const hasPostalAddress = Boolean(
      event.venueAddress ||
      event.venuePostalCode ||
      event.venueAddressLocality ||
      event.venueAddressRegion,
    );
    const offers = getAggregateOffer(event.signupPage, pages, eventUrl, locale);

    return [
      {
        '@type': 'Event',
        '@id': `${pageUrl}#event-${index + 1}`,
        name,
        ...(description && { description }),
        startDate,
        ...(image && { image: absoluteUrl(image, domain) }),
        eventAttendanceMode: isOnline
          ? 'https://schema.org/OnlineEventAttendanceMode'
          : 'https://schema.org/OfflineEventAttendanceMode',
        location: isOnline
          ? {
              '@type': 'VirtualLocation',
              url: eventUrl,
            }
          : {
              '@type': 'Place',
              ...(event.venueName && { name: event.venueName }),
              ...(hasPostalAddress && {
                address: {
                  '@type': 'PostalAddress',
                  ...(event.venueAddress && { streetAddress: event.venueAddress }),
                  ...(event.venuePostalCode && { postalCode: event.venuePostalCode }),
                  ...(event.venueAddressLocality && {
                    addressLocality: event.venueAddressLocality,
                  }),
                  ...(event.venueAddressRegion && { addressRegion: event.venueAddressRegion }),
                },
              }),
            },
        url: eventUrl,
        organizer: {
          '@type': 'Organization',
          name: 'IDA',
          url: domain,
        },
        ...(offers && { offers }),
        ...(performers.length > 0 && { performer: performers }),
        ...(locale && { inLanguage: locale }),
      },
    ];
  });

  if (graph.length === 0) {
    return null;
  }

  const jsonLd = {
    '@context': {
      '@vocab': 'https://schema.org/',
      priceText: 'https://ida.dk/events/ns#priceText',
    },
    '@graph': graph,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
    />
  );
};

export default ConferenceJsonLd;
